import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { z } from 'zod';

import { businessEmail, useTypedAppFormContext } from '@/components/Form';
import { FieldLabel } from '@/components/Form/fields';
import { Text } from '@/components/typography';
import { maxEmailLength, maxUserNameLength } from '@/constants/numbers';

import { type UserDetailFormType } from './UserFormType';

type Props = {
  isEditing: boolean;
  isSaving: boolean;
  isEmailEditable?: boolean;
};

const UserDetailInfoFields = ({ isEditing, isSaving, isEmailEditable }: Props) => {
  const form = useTypedAppFormContext({ defaultValues: {} as UserDetailFormType });

  return (
    <div className="flex w-full max-w-100 flex-col gap-6">
      <form.AppField
        name="name"
        validators={{
          onSubmit: z
            .string()
            .trim()
            .min(1, t`Name is required`),
        }}
      >
        {(field) => (
          <field.Field>
            <FieldLabel htmlFor="user-detail-name">
              <Trans>Name</Trans>
            </FieldLabel>
            {isEditing ? (
              <field.TextInput
                id="user-detail-name"
                autoComplete="name"
                spellCheck={false}
                autoCapitalize="words"
                autoFocus
                maxLength={maxUserNameLength}
                disabled={isSaving}
              />
            ) : (
              <Text variant="body1">{field.state.value}</Text>
            )}
            <field.Error />
          </field.Field>
        )}
      </form.AppField>
      <form.AppField
        name="email"
        validators={{ onSubmit: isEmailEditable ? businessEmail() : undefined }}
      >
        {(field) => (
          <field.Field>
            <FieldLabel htmlFor="user-detail-email">
              <Trans>Email</Trans>
            </FieldLabel>
            {isEditing && isEmailEditable ? (
              <field.TextInput
                id="user-detail-email"
                inputMode="email"
                placeholder={t`name@work-email.com`}
                autoComplete="email"
                spellCheck={false}
                autoCapitalize="none"
                maxLength={maxEmailLength}
                disabled={isSaving}
              />
            ) : (
              <Text variant="body1">{field.state.value}</Text>
            )}
            <field.Error />
          </field.Field>
        )}
      </form.AppField>
    </div>
  );
};

export default UserDetailInfoFields;
